import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function getOptionalUserId(): Promise<string | null> {
  try {
    const { getRequest } = await import("@tanstack/react-start/server");
    const req = getRequest();
    const auth = req?.headers.get("authorization") ?? null;
    if (!auth || !auth.startsWith("Bearer ")) return null;
    const token = auth.slice(7).trim();
    if (!token || token.split(".").length !== 3) return null;
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const { data, error } = await supabaseAdmin.auth.getUser(token);
    if (error || !data?.user) return null;
    return data.user.id;
  } catch {
    return null;
  }
}


export async function getFreeAnalysisCount(userId: string): Promise<number> {
  const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
  const { data: userDecks } = await supabaseAdmin
    .from("decks")
    .select("deck_content_hash, source, referrer_program_id, scores(id)")
    .eq("user_id", userId)
    .eq("source", "free")
    .is("referrer_program_id", null)
    .not("deck_content_hash", "is", null);
  const hashes = new Set<string>();
  for (const d of userDecks ?? []) {
    const s = d.scores as unknown as { id: string }[] | null;
    if (s && s.length && d.deck_content_hash) hashes.add(d.deck_content_hash);
  }
  return hashes.size;
}


// Analyze an uploaded deck: upload to storage, create deck row, score it.
// Input: { fileBase64, fileName, mimeType, anonToken?, ref?, forSubmission? } - returns { deckId }.
export const analyzeDeck = createServerFn({ method: "POST" })
  .inputValidator((input: { fileBase64: string; fileName: string; mimeType?: string; anonToken?: string; ref?: string; forSubmission?: boolean }) => {
    if (!input?.fileBase64 || typeof input.fileBase64 !== "string") {
      throw new Error("fileBase64 required");
    }
    if (!input?.fileName || typeof input.fileName !== "string") {
      throw new Error("fileName required");
    }
    const rawTok = typeof input.anonToken === "string" ? input.anonToken : "";
    const anonToken = rawTok.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64) || undefined;
    const rawRef = typeof input.ref === "string" ? input.ref : "";
    const ref = rawRef.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 64) || undefined;
    return {
      fileBase64: input.fileBase64,
      fileName: input.fileName,
      mimeType: input.mimeType ?? "application/pdf",
      anonToken,
      ref,
      forSubmission: Boolean(input.forSubmission),
    };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");



    const userId = await getOptionalUserId();

    // One-role-per-email: logged-in investors cannot run founder analyses.
    if (userId) {
      const { assertNotInvestor } = await import("./account-guards.server");
      await assertNotInvestor(userId);
      const { data: profile } = await supabaseAdmin
        .from("profiles").select("role").eq("id", userId).maybeSingle();
      if ((profile as { role?: string } | null)?.role === "investor") {
        throw new Error(
          "This account is registered as an investor and can't run founder analyses. Use a different email for a founder account.",
        );
      }
    }


    // Resolve ref → programs.id (only investor-owned programs count).
    let referrerProgramId: string | null = null;
    if (data.ref) {
      const { data: prog } = await supabaseAdmin
        .from("programs")
        .select("id, owner_user_id")
        .eq("ref_code", data.ref)
        .not("owner_user_id", "is", null)
        .maybeSingle();
      if (prog?.id) referrerProgramId = prog.id as string;
    }

    // Per-ref daily throttle: 20 free analyses per ref-code / day.
    if (referrerProgramId && data.ref) {
      const rlSubject = `ref:${data.ref}`;
      const { data: rlRow } = await supabaseAdmin
        .from("rate_limit" as any)
        .select("subject, generations_today, last_reset_at")
        .eq("subject", rlSubject)
        .maybeSingle();
      const now = new Date();
      let count = (rlRow as { generations_today?: number } | null)?.generations_today ?? 0;
      const lastResetIso = (rlRow as { last_reset_at?: string } | null)?.last_reset_at ?? null;
      const lastReset = lastResetIso ? new Date(lastResetIso) : null;
      if (!lastReset || now.getTime() - lastReset.getTime() > 24 * 60 * 60 * 1000) {
        count = 0;
      }
      if (count >= 20) {
        throw new Error("RATE_LIMITED: This referral link has hit today's limit. Please try again tomorrow.");
      }
      await supabaseAdmin
        .from("rate_limit" as any)
        .upsert({
          subject: rlSubject,
          generations_today: count + 1,
          last_reset_at: (lastReset && count > 0 ? lastReset : now).toISOString(),
        });
    }

    // 5-lifetime free cap - SKIP when a valid referrer_program_id resolved.
    if (userId && !data.forSubmission && !referrerProgramId) {
      const { isCapExempt } = await import("./cap-exempt.server");
      if (!(await isCapExempt(userId))) {
        const used = await getFreeAnalysisCount(userId);
        if (used >= 5) {
          throw new Error(
            "FREE_LIMIT: You've used your 5 free analyses. Submitting to investors has no limit.",

          );
        }
      }
    }


    const binary = atob(data.fileBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    // Synchronous UNREADABLE_PDF pre-check so users get an instant error
    // (not a spinner) for image-only / scanned PDFs.
    const { pdfLikelyHasText } = await import("./score-deck.server");
    const hasText = await pdfLikelyHasText(bytes);
    if (!hasText) {
      throw new Error(
        "UNREADABLE_PDF: We couldn't read any text from this PDF - it looks image-based or scanned. Please upload a text-based PDF (export your slides so the text is selectable).",
      );
    }

    // Synchronous daily-throttle pre-check (RATE_LIMITED = 5/day per subject).
    // Skip when a cached score already exists for these exact bytes.
    {
      const buf = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
      const hashBuf = await crypto.subtle.digest("SHA-256", buf);
      const hash = Array.from(new Uint8Array(hashBuf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const { data: cached } = await supabaseAdmin
        .from("scores")
        .select("id")
        .eq("deck_content_hash", hash)
        .limit(1)
        .maybeSingle();
      if (!cached) {
        const { isCapExempt } = await import("./cap-exempt.server");
        const exempt = userId ? await isCapExempt(userId) : false;
        if (!exempt) {
          const subject: string = userId ? String(userId) : `anon:${data.anonToken ?? "unknown"}`;
          const { data: rlRow } = await supabaseAdmin
            .from("rate_limit" as any)
            .select("subject, generations_today, last_reset_at")
            .eq("subject", subject)
            .maybeSingle();
          const rl = rlRow as { generations_today: number; last_reset_at: string } | null;
          const now = new Date();
          let count = rl?.generations_today ?? 0;
          const lastReset = rl?.last_reset_at ? new Date(rl.last_reset_at) : null;
          if (!lastReset || now.getTime() - lastReset.getTime() > 24 * 60 * 60 * 1000) {
            count = 0;
          }
          if (count >= 5) {
            throw new Error("RATE_LIMITED: You've hit today's analysis limit. Please try again tomorrow.");
          }
        }
      }
    }

    const deckId = crypto.randomUUID();
    const path = `anon/${deckId}/${data.fileName}`;

    const { error: upErr } = await supabaseAdmin.storage
      .from("decks")
      .upload(path, bytes, { contentType: data.mimeType, upsert: false });
    if (upErr) throw upErr;

    const { error: insErr } = await supabaseAdmin.from("decks").insert({
      id: deckId,
      file_name: data.fileName,
      file_path: path,
      status: "processing",
      anon_token: userId ? null : data.anonToken ?? null,
      source: data.forSubmission ? "application" : "free",
      referrer_program_id: referrerProgramId,
    });
    if (insErr) throw insErr;

    // Scoring happens in the background - triggered by the /results page via
    // scoreDeck(). analyzeDeck returns immediately so navigation is instant
    // and the client polls for the score.
    return { deckId };
  });

// Client-triggered background scoring. Idempotent: short-circuits if the
// deck already has a score (scoreDeckById also caches by content hash), and
// marks the deck failed on any error so it never gets stuck in 'processing'.
export const scoreDeck = createServerFn({ method: "POST" })
  .inputValidator((input: { deckId: string }) => {
    if (!input?.deckId) throw new Error("deckId required");
    return { deckId: String(input.deckId) };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const { data: deck } = await supabaseAdmin
      .from("decks")
      .select("id, status")
      .eq("id", data.deckId)
      .maybeSingle();
    if (!deck) return { status: "not_found" as const };
    if (deck.status === "scored") return { status: "scored" as const };

    // Check for an existing score row (idempotency against concurrent triggers)
    const { data: existing } = await supabaseAdmin
      .from("scores").select("id").eq("deck_id", data.deckId).limit(1).maybeSingle();
    if (existing) {
      await supabaseAdmin.from("decks").update({ status: "scored" }).eq("id", data.deckId);
      return { status: "scored" as const };
    }

    await supabaseAdmin.from("decks").update({ status: "processing" }).eq("id", data.deckId);
    try {
      const { scoreDeckById } = await import("./score-deck.server");
      await scoreDeckById(data.deckId);
      return { status: "scored" as const };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[scoreDeck] failed", msg);
      try {
        await supabaseAdmin.from("decks").update({ status: "failed" }).eq("id", data.deckId);
      } catch { /* ignore */ }
      return { status: "failed" as const, error: msg };
    }
  });

// Fetch current deck processing status - cheap, polled by /results.
export const getDeckStatus = createServerFn({ method: "GET" })
  .inputValidator((input: { deckId: string }) => {
    if (!input?.deckId) throw new Error("deckId required");
    return { deckId: String(input.deckId) };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const { data: deck } = await supabaseAdmin
      .from("decks")
      .select("status")
      .eq("id", data.deckId)
      .maybeSingle();
    return { status: (deck?.status ?? null) as string | null };
  });


// Fetch the latest score for a deck (public - used to render /results).
export const getDeckScore = createServerFn({ method: "GET" })
  .inputValidator((input: { deckId: string }) => {
    if (!input?.deckId) throw new Error("deckId required");
    return { deckId: input.deckId };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const { data: row, error } = await supabaseAdmin
      .from("scores")
      .select("id, overall_score, verdict, slide_flags, fixes, analysis, created_at")
      .eq("deck_id", data.deckId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return row;
  });

// Get a short-lived signed URL to the deck PDF (public by deckId).
export const getDeckFileUrl = createServerFn({ method: "GET" })
  .inputValidator((input: { deckId: string }) => {
    if (!input?.deckId) throw new Error("deckId required");
    return { deckId: input.deckId };
  })
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const { data: deck, error } = await supabaseAdmin
      .from("decks")
      .select("file_path")
      .eq("id", data.deckId)
      .maybeSingle();
    if (error || !deck?.file_path) return { url: null as string | null };
    const { data: signed, error: sErr } = await supabaseAdmin.storage
      .from("decks")
      .createSignedUrl(deck.file_path, 600);
    if (sErr || !signed?.signedUrl) return { url: null as string | null };
    return { url: signed.signedUrl };
  });

// Claim an anonymous deck for the current authenticated user.
export const claimDeck = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { deckId: string }) => {
    if (!input?.deckId) throw new Error("deckId required");
    return { deckId: input.deckId };
  })
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const { assertNotInvestor } = await import("./account-guards.server");
    const userId = context.userId;
    await assertNotInvestor(userId);
    const email =
      (context.claims as { email?: string } | undefined)?.email ?? null;

    await supabaseAdmin
      .from("profiles")
      .upsert({ id: userId, email: email ?? "" }, { onConflict: "id" });

    await supabaseAdmin
      .from("decks")
      .update({ user_id: userId })
      .eq("id", data.deckId)
      .eq("source", "free")
      .is("user_id", null);

    // Retry referral delivery (idempotent): analyze-anon-then-register path
    // may have skipped delivery because we had no founder email yet.
    try {
      const { data: deck } = await supabaseAdmin
        .from("decks")
        .select("referrer_program_id")
        .eq("id", data.deckId)
        .maybeSingle();
      if (deck?.referrer_program_id) {
        const { deliverReferralForDeck } = await import("./referral-delivery.server");
        await deliverReferralForDeck(data.deckId);
      }
    } catch (err) {
      console.error("[claim] referral delivery retry failed (non-fatal)", err);
    }

    return { ok: true };
  });


// Claim ALL anonymous decks from this browser (by anon_token) for the current user.
export const claimAnonDecks = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { anonToken: string }) => {
    const raw = typeof input?.anonToken === "string" ? input.anonToken : "";
    const anonToken = raw.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);
    if (!anonToken) throw new Error("anonToken required");
    return { anonToken };
  })
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/admin.server");
    const { assertNotInvestor } = await import("./account-guards.server");
    const userId = context.userId;
    await assertNotInvestor(userId);
    const email = (context.claims as { email?: string } | undefined)?.email ?? null;
    await supabaseAdmin
      .from("profiles")
      .upsert({ id: userId, email: email ?? "" }, { onConflict: "id" });
    const { data: claimed } = await supabaseAdmin
      .from("decks")
      .update({ user_id: userId })
      .eq("anon_token", data.anonToken)
      .eq("source", "free")
      .is("user_id", null)
      .select("id");
    return { ok: true, claimed: claimed?.length ?? 0 };
  });
